import React, { useRef, useEffect, useState, useCallback } from 'react';

/*
 * Renders a SQNCR session reading as a 1200x1200 PNG for posting to X.
 *
 * Deliberately drawn on a canvas in the browser rather than rasterised on the
 * server: it keeps the backend free of a native image dependency, and the card
 * comes out of the same rendering stack as the dashboard, so it looks like the
 * product rather than an approximation of it.
 *
 * Palette and type are lifted from #sqncr-tooltip in public/sqncr_3d.html.
 */

const SIZE = 1200;
const TEAL = '#37E6CF';
const MODE_COLOR = { buy: '#00ff55', dec: '#ff4444', hold: '#ffd400' };

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function prettyDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${MONTHS[Number(m) - 1]} ${Number(d)} ${y}`;
}

/** Load Orbitron once; the dashboard itself doesn't pull it in (only the iframe does). */
let fontPromise = null;

// Orbitron comes from Google Fonts. Behind a VPN, offline, or with the CDN
// blocked that request can hang rather than fail, so the load is raced against a
// timeout — a card must never be gated on a webfont it may never receive.
const FONT_TIMEOUT_MS = 2500;

function ensureFont() {
  if (fontPromise) return fontPromise;
  fontPromise = new Promise((resolve) => {
    const href = 'https://fonts.googleapis.com/css2?family=Orbitron:wght@500;600;700;800&display=swap';
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    }
    if (!document.fonts) return resolve(false);

    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    const timer = setTimeout(() => done(false), FONT_TIMEOUT_MS);

    Promise.all([
      document.fonts.load('700 120px Orbitron'),
      document.fonts.load('500 40px Orbitron'),
    ])
      .then(() => document.fonts.ready)
      .then(() => { clearTimeout(timer); done(true); })
      .catch(() => { clearTimeout(timer); done(false); });
  });
  return fontPromise;
}

const orbitronReady = () => {
  try { return Boolean(document.fonts && document.fonts.check('700 120px Orbitron')); }
  catch (_) { return false; }
};

/*
 * ctx.letterSpacing is not in Safari before 17.4 and the SQNCR look depends on
 * wide tracking, so spacing is applied per character instead of trusting it.
 */
function tracked(ctx, text, x, y, spacing) {
  let cx = x;
  for (const ch of String(text)) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
  return cx - x - spacing;
}

function trackedWidth(ctx, text, spacing) {
  let w = 0;
  for (const ch of String(text)) w += ctx.measureText(ch).width + spacing;
  return w - spacing;
}

/*
 * Volume profile card — the market-profile drawing from the Info page
 * (InfoContent.js "30-DAY MARKET PROFILE"), but plotted from the live
 * distribution instead of its illustrative fixed bar widths.
 *
 * Palette is taken from that SVG: value-area fill at 4% white, slab bars
 * #444455 inside the value area and progressively darker outside, POC bar
 * rgba(204,34,34,0.90) with a #ff4444 label, VAH/VAL dashed at
 * rgba(85,85,102,0.90), current price in green.
 */
function drawProfile(ctx, card, F) {
  const X = 150, W = 620;               // bar origin and max bar width
  const TOP = 300, H = 620;             // plot area
  const rows = card.rows || [];

  const hi = card.profile_high, lo = card.profile_low;
  const toY = (p) => TOP + H - ((p - lo) / (hi - lo)) * H;

  if (!rows.length) {
    ctx.fillStyle = '#3c5450';
    ctx.font = F(500, 26);
    tracked(ctx, 'LEVELS ONLY — DISTRIBUTION UNAVAILABLE', X, TOP + H / 2, 4);
  }

  // Value area fill
  if (rows.length) {
    const vahY = toY(card.vah), valY = toY(card.val);
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.fillRect(X, vahY, W + 90, valY - vahY);
  }

  // Bars
  const rowH = rows.length ? H / rows.length : 0;
  rows.forEach((r, i) => {
    const y = TOP + i * rowH;
    const w = Math.max(3, r.ratio * W);
    if (r.is_poc)      ctx.fillStyle = 'rgba(204,34,34,0.90)';
    else if (r.in_va)  ctx.fillStyle = '#444455';
    else if (r.ratio > 0.35) ctx.fillStyle = '#2a2a3a';
    else if (r.ratio > 0.18) ctx.fillStyle = '#222233';
    else               ctx.fillStyle = '#1c1c2a';
    ctx.fillRect(X, y + 1.5, w, Math.max(rowH - 3, 2));
  });

  // Level lines + labels
  const line = (price, stroke, label, labelColor, dash) => {
    if (!rows.length) return;
    const y = toY(price);
    ctx.save();
    ctx.setLineDash(dash);
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(X, y);
    ctx.lineTo(X + W + 90, y);
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = labelColor;
    ctx.font = F(500, 24);
    tracked(ctx, label, X + W + 104, y + 8, 2);
  };

  line(card.vah, 'rgba(85,85,102,0.90)', `VAH  $${Number(card.vah).toLocaleString('en-US')}`, 'rgba(170,170,190,0.75)', [6, 10]);
  line(card.poc, 'rgba(204,34,34,0.65)', `POC  $${Number(card.poc).toLocaleString('en-US')}`, '#ff4444', [6, 10]);
  line(card.val, 'rgba(85,85,102,0.90)', `VAL  $${Number(card.val).toLocaleString('en-US')}`, 'rgba(170,170,190,0.75)', [6, 10]);

  /*
   * On a migrating value area, mark the second shelf. Without it the card reads
   * "VALUE MIGRATING UP" with nothing on the chart to point at, and the shelf
   * driving that call sits unlabelled outside the value-area band.
   */
  if (card.migration && rows.length) {
    const m = card.migration;
    line(m.node.price, '#ffd400', `SHELF  $${Number(m.node.price).toLocaleString('en-US')}`, '#ffd400', [12, 8]);
    if (m.gap) {
      line(m.gap.price, 'rgba(255,212,0,0.35)', `GAP  $${Number(m.gap.price).toLocaleString('en-US')}`, 'rgba(255,212,0,0.55)', [2, 12]);
    }
  }

  // Current price — solid green, the one live level on the chart
  if (rows.length && card.price) {
    const y = toY(card.price);
    ctx.strokeStyle = 'rgba(0,229,122,0.85)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(X, y);
    ctx.lineTo(X + W + 90, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(X - 14, y, 7, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,229,122,0.85)';
    ctx.fill();
    ctx.font = F(700, 24);
    tracked(ctx, `BTC  $${Number(card.price).toLocaleString('en-US')}`, X + W + 104, y + 8, 2);
  }

  // Volume axis
  ctx.fillStyle = '#545a72';
  ctx.font = F(500, 20);
  tracked(ctx, 'VOLUME / TIME →', X, TOP + H + 44, 3);
}


/*
 * Two-year profile — the TPO/VP viewer's hollow treatment (public/tpo_vp.html),
 * which is a different drawing from the Info page's filled slabs on purpose:
 * the 30-day card reads as a solid block of recent acceptance, the two-year
 * card as an outlined structure you read levels off.
 *
 * Palette straight from that file: bars barely-there #121212 at 0.65, the POC
 * bar stroked rather than filled, a #2a2a2a silhouette tracing the bar tips,
 * POC line solid #cccccc, VAH/VAL dashed #484848 with the viewer's header
 * colours (#26c49a / #c0392b) on their labels, price in #00e5ff.
 */
function drawProfileHollow(ctx, card, F) {
  const X = 150, W = 620;
  const TOP = 300, H = 620;
  const rows = card.rows || [];

  const hi = card.profile_high, lo = card.profile_low;
  const toY = (p) => TOP + H - ((p - lo) / (hi - lo)) * H;

  if (!rows.length) {
    ctx.fillStyle = '#3c5450';
    ctx.font = F(500, 26);
    tracked(ctx, 'LEVELS ONLY — DISTRIBUTION UNAVAILABLE', X, TOP + H / 2, 4);
    return;
  }

  const rowH = H / rows.length;

  // Bars — hollow: a near-black fill for everything, an outline for the POC.
  rows.forEach((r, i) => {
    const y = TOP + i * rowH;
    const w = Math.max(3, r.ratio * W);
    const bh = Math.max(rowH - 1.5, 1);
    if (r.is_poc) {
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 2.5;
      ctx.globalAlpha = 1;
      ctx.strokeRect(X + 1.25, y + 1.25, w - 2.5, bh - 2.5);
    } else {
      ctx.fillStyle = '#121212';
      ctx.globalAlpha = 0.65;
      ctx.fillRect(X, y, w, bh);
    }
  });
  ctx.globalAlpha = 1;

  // Silhouette through the bar tips — the shape of the distribution.
  ctx.beginPath();
  ctx.strokeStyle = '#2a2a2a';
  ctx.lineWidth = 2.5;
  rows.forEach((r, i) => {
    const x = X + Math.max(3, r.ratio * W);
    const y = TOP + i * rowH + rowH / 2;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.stroke();

  /*
   * Volume nodes instead of the VAH/POC/VAL triad. Over two years the value
   * area is so wide that its edges say little; the shelves price stalled on and
   * the gaps it moved through are what describe the structure.
   *
   * Filled diamond = high-volume node, hollow = low-volume node, matching the
   * card's hollow-vs-filled language elsewhere.
   */
  const money = (n) => `$${Number(n).toLocaleString('en-US')}`;

  const diamond = (cx, cy, r, filled, color) => {
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r, cy);
    ctx.closePath();
    if (filled) { ctx.fillStyle = color; ctx.fill(); }
    else { ctx.strokeStyle = color; ctx.lineWidth = 2.5; ctx.stroke(); }
  };

  /*
   * Lines first, labels second.
   *
   * A node and the price line can resolve to within a few pixels of each other
   * — $78,565 and $77,701 are 7px apart at this scale — and drawing each label
   * as its line is drawn stacks them illegibly. Collect every label, then place
   * them top-down enforcing a minimum gap, the same nudge the TPO/VP viewer
   * applies via its usedY list.
   *
   * The price line also stops short of the node lines so it reads as its own
   * mark rather than another level in the same column.
   */
  const LINE_END = X + W + 90;
  const PRICE_LINE_END = X + W + 20;
  const LABEL_X = LINE_END + 14;
  const LABEL_GAP = 30;
  const labels = [];

  const node = (n, isHigh) => {
    const y = toY(n.price);
    if (y < TOP - 2 || y > TOP + H + 2) return;
    const barW = Math.max(3, (n.rel || 0) * W);

    ctx.save();
    ctx.setLineDash(isHigh ? [4, 10] : [2, 12]);
    ctx.strokeStyle = isHigh ? '#585858' : '#3a3a3a';
    ctx.lineWidth = 2;
    ctx.globalAlpha = isHigh ? 0.5 : 0.65;
    ctx.beginPath();
    ctx.moveTo(X, y);
    ctx.lineTo(LINE_END, y);
    ctx.stroke();
    ctx.restore();

    ctx.globalAlpha = isHigh ? 0.8 : 1;
    diamond(X + barW, y, 8, isHigh, isHigh ? '#888' : '#6a6a6a');
    ctx.globalAlpha = 1;

    labels.push({
      y, text: `${isHigh ? 'HVN' : 'LVN'}  ${money(n.price)}`,
      color: isHigh ? '#cccccc' : '#6a6a6a', weight: isHigh ? 700 : 500,
    });
  };

  (card.lvns || []).forEach((n) => node(n, false));
  (card.hvns || []).forEach((n) => node(n, true));

  // Price — the one live level on the chart, deliberately a shorter line
  if (card.price) {
    const y = toY(card.price);
    ctx.strokeStyle = '#00e5ff';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(X, y);
    ctx.lineTo(PRICE_LINE_END, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(X - 16, y, 8, 0, Math.PI * 2);
    ctx.fillStyle = '#00e5ff';
    ctx.fill();
    labels.push({ y, text: `BTC  ${money(card.price)}`, color: '#00e5ff', weight: 700 });
  }

  // Place top-down, pushing each label clear of the one above it.
  labels.sort((a, b) => a.y - b.y);
  let lastY = -Infinity;
  labels.forEach((l) => {
    let ly = Math.max(l.y + 7, lastY + LABEL_GAP);
    lastY = ly;
    ctx.fillStyle = l.color;
    ctx.font = F(l.weight, 22);
    tracked(ctx, l.text, LABEL_X, ly, 2);
  });

  // Legend
  ctx.globalAlpha = 0.8;
  diamond(X + 10, TOP + H + 40, 7, true, '#888');
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#585858';
  ctx.font = F(500, 19);
  tracked(ctx, 'HIGH VOLUME NODE', X + 26, TOP + H + 47, 2);
  diamond(X + 300, TOP + H + 40, 7, false, '#6a6a6a');
  ctx.fillStyle = '#4a4a4a';
  tracked(ctx, 'LOW VOLUME NODE', X + 316, TOP + H + 47, 2);

  ctx.fillStyle = '#2e2e2e';
  ctx.font = F(500, 18);
  tracked(ctx, 'VOLUME / TIME →', X, TOP + H + 84, 3);
}

export function drawCard(canvas, card, fontReady) {
  const ctx = canvas.getContext('2d');
  const F = (weight, px) => `${weight} ${px}px ${fontReady ? "'Orbitron', " : ''}sans-serif`;
  const color = card.color || MODE_COLOR[card.mode] || '#aaaaaa';

  const twoYear = card.kind === 'vpo2y';

  // Backdrop — the 2Y card matches the TPO/VP viewer's ground, not the SQNCR's.
  ctx.fillStyle = twoYear ? '#060606' : '#050505';
  ctx.fillRect(0, 0, SIZE, SIZE);

  if (card.kind === 'vpo' || twoYear) {
    ctx.strokeStyle = 'rgba(55,230,207,0.15)';
    ctx.lineWidth = 3;
    ctx.strokeRect(61.5, 61.5, SIZE - 123, SIZE - 123);
    ctx.fillStyle = 'rgba(55,230,207,0.45)';
    ctx.fillRect(60, 60, SIZE - 120, 6);

    ctx.fillStyle = '#9ccfc9';
    ctx.font = F(700, 40);
    tracked(ctx, 'VOLUME PROFILE', 150, 168, 9);

    ctx.fillStyle = '#4a6660';
    ctx.font = F(500, 26);
    const horizon = card.kind === 'vpo2y'
      ? `TWO YEAR  ·  ${card.days_covered || 730} DAYS`
      : '30-DAY';
    tracked(ctx, `${horizon}  ·  ${prettyDate(card.date)}`, 150, 216, 6);

    ctx.fillStyle = card.color || '#aaaaaa';
    ctx.font = F(700, 44);
    tracked(ctx, card.position || '', 150, 272, 10);

    if (twoYear) {
      // The viewer labels its horizon in the header; mirror that.
      // Baseline-aligned with the XYR mark at the foot of the card; the volume
      // axis label already occupies y≈964.
      ctx.fillStyle = '#2a2a2a';
      ctx.font = F(500, 20);
      tracked(ctx, `BTC/USDT  ·  VP  ·  ${Math.round(card.profile_low).toLocaleString()}–${Math.round(card.profile_high).toLocaleString()}`, 150, SIZE - 110, 3);
    }

    if (twoYear) drawProfileHollow(ctx, card, F);
    else drawProfile(ctx, card, F);

    ctx.fillStyle = 'rgba(55,230,207,0.55)';
    ctx.font = F(800, 36);
    const wm = trackedWidth(ctx, 'XYR', 12);
    tracked(ctx, 'XYR', SIZE - 150 - wm, SIZE - 110, 12);
    return;
  }

  // Panel — the floating window itself
  const PX = 90, PY = 150, PW = SIZE - PX * 2, PH = 900;
  ctx.fillStyle = 'rgba(6,6,6,0.94)';
  ctx.fillRect(PX, PY, PW, PH);
  ctx.strokeStyle = 'rgba(55,230,207,0.15)';
  ctx.lineWidth = 3;
  ctx.strokeRect(PX + 1.5, PY + 1.5, PW - 3, PH - 3);
  // Brighter top rule, as in the tooltip
  ctx.fillStyle = 'rgba(55,230,207,0.45)';
  ctx.fillRect(PX, PY, PW, 6);

  const L = PX + 62;

  // Session + gate
  ctx.fillStyle = '#9ccfc9';
  ctx.font = F(700, 44);
  tracked(ctx, `${card.session} SESSION`, L, PY + 150, 9);

  ctx.fillStyle = '#4a6660';
  ctx.font = F(500, 30);
  tracked(ctx, card.gate, L, PY + 208, 7);

  // The reading
  const sign = card.r >= 0 ? '+' : '';
  ctx.fillStyle = color;
  ctx.font = F(800, 168);
  tracked(ctx, `R ${sign}${Number(card.r).toFixed(2)}`, L, PY + 430, 8);

  ctx.font = F(700, 46);
  tracked(ctx, card.mode_label, L, PY + 510, 12);

  // Divider
  ctx.fillStyle = 'rgba(255,255,255,0.07)';
  ctx.fillRect(L, PY + 570, PW - 124, 2);

  // Context
  ctx.fillStyle = '#cfd8d6';
  ctx.font = F(600, 46);
  if (card.price) tracked(ctx, `BTC  $${Number(card.price).toLocaleString('en-US')}`, L, PY + 650, 5);

  ctx.fillStyle = '#4a6660';
  ctx.font = F(500, 28);
  tracked(ctx, prettyDate(card.date), L, PY + 706, 6);

  if (card.previous_r !== null && card.previous_r !== undefined) {
    const d = card.delta;
    const dTxt = d === null || d === undefined
      ? ''
      : `   ${d >= 0 ? '+' : ''}${Number(d).toFixed(2)}`;
    ctx.fillStyle = '#3c5450';
    ctx.font = F(500, 26);
    tracked(ctx, `PREV ${card.previous_r >= 0 ? '+' : ''}${Number(card.previous_r).toFixed(2)}${dTxt}`, L, PY + 758, 5);
  }

  // Mark
  ctx.fillStyle = 'rgba(55,230,207,0.55)';
  ctx.font = F(800, 40);
  const w = trackedWidth(ctx, 'XYR', 12);
  tracked(ctx, 'XYR', PX + PW - 62 - w, PY + PH - 58, 12);

  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.font = F(500, 22);
  tracked(ctx, 'SEQUENCER', L, PY + PH - 58, 6);
}

export default function SessionCard({ card, filename }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let alive = true;
    /*
     * Draw straight away with whatever font is already available, then redraw in
     * Orbitron once it loads. Previously the only draw call sat inside the font
     * promise, so a webfont that never resolved left the canvas blank with no
     * error anywhere — the card simply didn't appear.
     */
    if (ref.current) {
      drawCard(ref.current, card, orbitronReady());
      setReady(true);
    }
    ensureFont().then((ok) => {
      if (!alive || !ref.current) return;
      if (ok) drawCard(ref.current, card, true);
      setReady(true);
    });
    return () => { alive = false; };
  }, [card]);

  const download = useCallback(() => {
    const name = filename || `xyr-${card.kind || 'session'}-${card.date}-${card.session_short || ''}.png`
      .replace(/-\.png$/, '.png');
    ref.current.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  }, [card, filename]);

  /* Put the PNG on the clipboard so it can be pasted straight into the X composer. */
  const copyImage = useCallback(() => {
    if (!navigator.clipboard || !window.ClipboardItem) {
      setCopied('clipboard images unsupported — use Download');
      setTimeout(() => setCopied(''), 4000);
      return;
    }
    ref.current.toBlob((blob) => {
      navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })])
        .then(() => setCopied('image copied'))
        .catch(() => setCopied('copy blocked — use Download'))
        .finally(() => setTimeout(() => setCopied(''), 4000));
    }, 'image/png');
  }, []);

  const btn = {
    fontFamily: 'monospace', fontSize: 9, letterSpacing: '1.5px', textTransform: 'uppercase',
    padding: '5px 11px', cursor: 'pointer', background: 'transparent',
    border: '1px solid #37E6CF55', color: TEAL, borderRadius: 2, marginRight: 6,
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <canvas
        ref={ref} width={SIZE} height={SIZE}
        style={{
          width: 300, height: 300, display: 'block', borderRadius: 3,
          border: '1px solid #1a1a1a', marginBottom: 8,
          opacity: ready ? 1 : 0.2, transition: 'opacity .2s',
        }}
      />
      <button style={btn} onClick={download}>Download PNG</button>
      <button style={btn} onClick={copyImage}>Copy image</button>
      {card.alt && (
        <button
          style={btn}
          onClick={() => {
            navigator.clipboard.writeText(card.alt)
              .then(() => setCopied('alt text copied — paste into X’s ALT field'))
              .catch(() => setCopied('clipboard blocked'))
              .finally(() => setTimeout(() => setCopied(''), 5000));
          }}
        >Copy alt</button>
      )}
      {copied && (
        <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#4a6660', letterSpacing: '1px' }}>
          {copied}
        </span>
      )}
    </div>
  );
}
